import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Chip, IconButton, Pagination, Paper, Skeleton, Tooltip, Typography } from '@mui/material';
import { MdCheckCircle, MdDoneAll, MdInventory2, MdLocalShipping, MdNotificationsNone, MdRefresh, MdStorefront } from 'react-icons/md';
import { getData, patchData } from '../../Webservice/webservice';

const FONT = 'Mulish, sans-serif';
const authToken = () => { const raw = sessionStorage.getItem('token') || localStorage.getItem('sellerToken'); return raw ? `Bearer ${raw}` : ''; };
const tellBell = () => window.dispatchEvent(new Event('seller-notifications-changed'));

// what each kind of notification looks like, and where it leads
const KIND = {
  seller_order: { label: 'Orders', icon: <MdLocalShipping size={22} />, color: '#6366f1', bg: '#e0e7ff', go: (n) => (n.linkId ? `/orders/${n.linkId}` : '/orders') },
  seller_request: { label: 'Supply requests', icon: <MdStorefront size={22} />, color: '#f59e0b', bg: '#fef3c7', go: () => ({ to: '/products', state: { tab: 'requests' } }) },
  product: { label: 'Products', icon: <MdInventory2 size={22} />, color: '#0ea5e9', bg: '#e0f2fe', go: () => '/products' },
};
const FALLBACK = { label: 'Update', icon: <MdNotificationsNone size={22} />, color: '#64748b', bg: '#f1f5f9', go: () => null };
const kindOf = (t) => KIND[t] || FALLBACK;

const FILTERS = [['', 'All'], ['unread', 'Unread'], ['seller_order', 'Orders'], ['seller_request,product', 'Products and requests']];

const ago = (d) => {
  const sec = Math.max(0, Math.floor((Date.now() - new Date(d).getTime()) / 1000));
  if (sec < 60) return 'Just now';
  if (sec < 3600) return `${Math.floor(sec / 60)} min ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)} h ago`;
  if (sec < 172800) return 'Yesterday';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: sec > 31536000 ? 'numeric' : undefined });
};
const dayOf = (d) => {
  const day = new Date(d); day.setHours(0, 0, 0, 0);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((today - day) / 86400000);
  return diff <= 0 ? 'Today' : diff === 1 ? 'Yesterday' : diff < 7 ? 'This week' : 'Earlier';
};

const Notification = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ total: 0, totalPages: 1, unread: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (filter === 'unread') params.isRead = false;
      else if (filter) params.type = filter;
      const res = await getData('seller/notification/vend', params, authToken());
      setItems(res?.data || []);
      setMeta(res?.meta || { total: 0, totalPages: 1, unread: 0 });
      setError('');
    } catch (e) {
      setError(e?.message || 'Could not load your notifications.');
      setItems([]);
    } finally { setLoading(false); }
  }, [page, filter]);
  useEffect(() => { load(); }, [load]);

  const open = async (n) => {
    if (!n.isRead) {
      setItems((list) => list.map((x) => (x._id === n._id ? { ...x, isRead: true } : x)));
      setMeta((m) => ({ ...m, unread: Math.max(0, (m.unread || 0) - 1) }));
      patchData(`seller/notification/read/${n._id}`, {}, authToken()).then(tellBell).catch(() => load());
    }
    const target = kindOf(n.type).go(n);
    if (!target) return;
    if (typeof target === 'string') navigate(target); else navigate(target.to, { state: target.state });
  };

  const markAll = async () => {
    setBusy(true);
    try {
      await patchData('seller/notification/mark-all-read?userModel=Seller', {}, authToken());
      setItems((list) => list.map((x) => ({ ...x, isRead: true })));
      setMeta((m) => ({ ...m, unread: 0 }));
      tellBell();
    } catch (e) { setError(e?.message || 'Could not mark them as read.'); } finally { setBusy(false); }
  };

  // keep the order, but show them under Today, Yesterday and so on
  const groups = useMemo(() => {
    const out = [];
    items.forEach((n) => {
      const label = dayOf(n.createdAt);
      const last = out[out.length - 1];
      if (last && last.label === label) last.items.push(n); else out.push({ label, items: [n] });
    });
    return out;
  }, [items]);

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, maxWidth: 900, mx: 'auto', fontFamily: FONT }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 24, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
            Notifications
            {meta.unread > 0 && <Chip size="small" label={`${meta.unread} new`} sx={{ fontFamily: FONT, fontWeight: 800, background: '#ef4444', color: '#fff' }} />}
          </Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: '#64748b' }}>New orders, supply request decisions and product updates.</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button data-tour="vendor-notification-markall" startIcon={<MdDoneAll />} onClick={markAll} disabled={busy || !meta.unread} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px', border: '1px solid #cbd5e1', color: '#0f172a', px: 2 }}>Mark all as read</Button>
          <Tooltip title="Refresh"><IconButton onClick={load} sx={{ background: '#fff', border: '1px solid #e2e8f0' }}><MdRefresh /></IconButton></Tooltip>
        </Box>
      </Box>

      <Box data-tour="vendor-notification-unread" sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        {FILTERS.map(([k, label]) => (
          <Chip key={label} clickable label={label} onClick={() => { setFilter(k); setPage(1); }} sx={{ fontFamily: FONT, fontWeight: 800, background: filter === k ? '#0f172a' : '#fff', color: filter === k ? '#fff' : '#334155', border: '1px solid #cbd5e1' }} />
        ))}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')} action={<Button color="inherit" size="small" onClick={load}>Try again</Button>}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'grid', gap: 1.25 }}>{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} variant="rounded" height={78} sx={{ borderRadius: '14px' }} />)}</Box>
      ) : items.length === 0 ? (
        <Box sx={{ py: 9, textAlign: 'center', color: '#64748b', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px' }}>
          <MdCheckCircle size={40} color="#cbd5e1" />
          <Typography sx={{ fontFamily: FONT, fontWeight: 700, mt: 1 }}>{filter === 'unread' ? 'You are all caught up' : filter ? 'Nothing here' : 'No notifications yet'}</Typography>
          {filter && <Button onClick={() => { setFilter(''); setPage(1); }} sx={{ textTransform: 'none', fontWeight: 700, mt: 1 }}>Show everything</Button>}
        </Box>
      ) : (
        groups.map((g) => (
          <Box key={g.label} data-tour="vendor-notification-list" sx={{ mb: 2.5 }}>
            <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 12, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.8px', mb: 0.75, ml: 0.5 }}>{g.label}</Typography>
            <Paper elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: '14px', overflow: 'hidden' }}>
              {g.items.map((n, i) => {
                const k = kindOf(n.type);
                const clickable = !!k.go(n) || !n.isRead;
                return (
                  <Box key={n._id} onClick={() => clickable && open(n)}
                    sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', p: 1.75, cursor: clickable ? 'pointer' : 'default', borderBottom: i < g.items.length - 1 ? '1px solid #f1f5f9' : 'none', background: n.isRead ? '#fff' : '#f5f7ff', transition: 'background .15s', '&:hover': clickable ? { background: '#f8fafc' } : undefined }}>
                    <Box sx={{ width: 42, height: 42, borderRadius: '12px', display: 'grid', placeItems: 'center', flexShrink: 0, background: k.bg, color: k.color }}>{k.icon}</Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, alignItems: 'baseline' }}>
                        <Typography sx={{ fontFamily: FONT, fontWeight: n.isRead ? 700 : 800, fontSize: 14.5, color: '#0f172a' }}>{n.title}</Typography>
                        <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#94a3b8', whiteSpace: 'nowrap' }}>{ago(n.createdAt)}</Typography>
                      </Box>
                      <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: '#64748b', lineHeight: 1.5, mt: 0.25 }}>{n.message}</Typography>
                      <Chip size="small" label={k.label} sx={{ mt: 0.75, height: 20, fontFamily: FONT, fontWeight: 700, fontSize: 11, background: k.bg, color: k.color }} />
                    </Box>
                    {!n.isRead && <Box title="New" sx={{ width: 10, height: 10, borderRadius: '50%', background: '#4f46e5', mt: 0.75, flexShrink: 0 }} />}
                  </Box>
                );
              })}
            </Paper>
          </Box>
        ))
      )}

      {meta.totalPages > 1 && <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}><Pagination count={meta.totalPages} page={page} onChange={(_, v) => { setPage(v); window.scrollTo({ top: 0, behavior: 'smooth' }); }} shape="rounded" /></Box>}
    </Box>
  );
};

export default Notification;
